import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components/macro';
import { useTable } from 'react-table';
import { useCustomCellStyles } from 'Common/hooks/useCustomCellStyles';
import { useResponsiveTable } from 'Common/hooks/useResponsiveTable';
import { DefaultTable } from 'Common/components/table';
import {
    CheckRounded,
    CloseRounded,
    MailRounded,
    UpdateRounded,
    WarningRounded,
} from '@material-ui/icons';
import { ifProp } from 'styled-tools';
import Draggable from 'react-draggable';
import InnerBox from 'Common/components/Boxes/InnerBox';
import colors from 'Common/constants/colors';
import { Tooltip } from '@material-ui/core';
import parseDate from 'Common/utils/parseDate';
import formatDate from 'Common/utils/formatDate';
import defaultDateTimeFormat from 'Common/constants/defaultDateTimeFormat';

const Wrapper = styled(InnerBox)`
    margin-top: 20px;
    padding: 0 10px 10px;
    border-top: 10px solid ${colors.INNER_BOX_BACKGROUND};
    border-radius: 4px;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    max-height: 342px;

    @media screen and (min-width: 1090px) {
        padding: 0 20px 20px;
        border-top-width: 20px;
    }
`;

const BorderlessButton = styled.button`
    background: none;
    border: 0 none;
    outline: 0 none;
    padding: 0;
    margin: 0;
    color: ${ifProp('isActive', colors.PRIMARY, colors.LIGHTER_GRAY)};
    cursor: pointer;
`;

const EmailIconButton = styled(BorderlessButton)`
    display: inline-flex;
    vertical-align: middle;
`;

const DraggableContentWrapper = styled.div`
    position: fixed;
    z-index: 9999;
    cursor: move;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(0, 0, 0, 0.17);
    border-radius: 6px;
    overflow: hidden;
`;

const DraggableContent = styled.div`
    width: 100%;
    max-width: 550px;
    min-width: 350px;
    background: ${colors.DIRTY_WHITE};
    padding: 20px 50px 20px 20px;
`;

const DraggableContentCloseButton = styled(BorderlessButton)`
    position: absolute;
    top: 0;
    right: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;

    svg {
        margin-right: 0;
        color: ${colors.DARK_GRAY};
        font-size: 25px !important;
    }
`;

const Label = styled.div`
    font-size: 18px;
    font-weight: 300;
    line-height: 27px;
    margin-bottom: 5px;
    color: ${colors.DARK_GRAY};
`;

const RecipientsList = styled.div`
    margin-bottom: 15px;
`;

const Recipient = styled.div`
    font-size: 18px;
    font-weight: 300;
    line-height: 27px;
    margin-bottom: 5px;
    color: ${colors.DARK_GRAY};

    &:last-child {
        margin-bottom: 0;
    }
`;

const EmailSubject = styled.div`
    margin-bottom: 15px;
    font-size: 18px;
    font-weight: 300;
    line-height: 27px;
    color: ${colors.DARK_GRAY};
`;

const EmailBody = styled.div`
    font-size: 14px;
    font-weight: 300;
    line-height: 21px;
    color: ${colors.DARK_GRAY};
`;

const PendingNotInitiatedIcon = styled(UpdateRounded)`
    color: ${colors.LIGHTER_GRAY};
`;

const PendingInProgressIcon = styled(UpdateRounded)`
    color: ${colors.WARNING};
`;

const OverdueIcon = styled(WarningRounded)`
    color: ${colors.WARNING};
`;

const CompletedIcon = styled(CheckRounded)`
    color: ${colors.GREEN};
`;

const StyledTable = styled(DefaultTable)`
    th {
        top: 0;
        background: ${colors.INNER_BOX_BACKGROUND};
    }
`;

const DriverLicenseSentEmailsTable = ({ driverLicenseControl, sentEmails }) => {
    const [openedEmailId, setOpenedEmailId] = useState(null);

    const handleEmailButtonClick = sentEmailId => {
        setOpenedEmailId(prevOpenedEmailId => {
            if (prevOpenedEmailId === sentEmailId) {
                return null;
            }

            return sentEmailId;
        });
    };

    const handleEmailClose = () => {
        setOpenedEmailId(null);
    };

    const columns = useMemo(
        () => [
            {
                Header: '',
                accessor: 'emailIcon',
                customCellStyles: {
                    width: '58px',
                    textAlign: 'center',
                },
            },
            {
                Header: 'Kontrollinformation',
                accessor: 'information',
            },
            {
                Header: 'Zeitstempel',
                accessor: 'sentAt',
            },
            {
                Header: 'Empfänger',
                accessor: 'recipient',
            },
            {
                Header: 'Status',
                accessor: 'status',
                customCellStyles: {
                    textAlign: 'center',
                },
            },
        ],
        [],
    );

    const employee = driverLicenseControl.employee;

    const data = useMemo(
        () =>
            sentEmails.map(sentEmail => {
                const isEmployeeRecipient =
                    sentEmail.recipients.length === 1 &&
                    sentEmail.recipients[0].user.id === employee.user.id;

                let StatusIcon;
                let statusTitle;

                if (sentEmail.isPendingNotInitiated) {
                    StatusIcon = PendingNotInitiatedIcon;
                    statusTitle = 'Ausstehend (nicht initiiert)';
                }

                if (sentEmail.isPendingInitiated) {
                    StatusIcon = PendingInProgressIcon;
                    statusTitle = 'Ausstehend (in Bearbeitung)';
                }

                if (sentEmail.isCompleted) {
                    StatusIcon = CompletedIcon;
                    statusTitle = 'Abgeschlossen';
                }

                if (sentEmail.isOverdue) {
                    StatusIcon = OverdueIcon;
                    statusTitle = 'Überfällig';
                }

                return {
                    emailIcon: (
                        <EmailIconButton
                            type="button"
                            onClick={() => handleEmailButtonClick(sentEmail.id)}
                            isActive={openedEmailId === sentEmail.id}
                        >
                            <MailRounded />
                        </EmailIconButton>
                    ),
                    information: (
                        <BorderlessButton
                            type="button"
                            onClick={() => handleEmailButtonClick(sentEmail.id)}
                            isActive={openedEmailId === sentEmail.id}
                        >
                            {sentEmail.sendingReason.name}
                        </BorderlessButton>
                    ),
                    sentAt: formatDate(
                        parseDate(sentEmail.sentAt),
                        defaultDateTimeFormat,
                    ),
                    recipient: isEmployeeRecipient
                        ? `${employee.lastName}, ${employee.firstName}`
                        : 'Zugewiesene Prüfer',
                    status: StatusIcon ? (
                        <Tooltip title={statusTitle}>
                            <StatusIcon />
                        </Tooltip>
                    ) : (
                        '-'
                    ),
                };
            }),
        [
            sentEmails,
            employee.user.id,
            employee.firstName,
            employee.lastName,
            openedEmailId,
        ],
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
        visibleColumns,
        state: { hiddenColumns },
    } = useTable(
        {
            columns,
            data,
            hideColumnsPriority: ['sentAt', 'emailIcon', 'recipient', 'status'],
        },
        useCustomCellStyles,
        useResponsiveTable,
    );

    const openedEmail =
        openedEmailId !== null
            ? sentEmails.find(sentEmail => sentEmail.id === openedEmailId)
            : null;

    return (
        <Wrapper>
            {!!openedEmail && (
                <Draggable cancel=".draggable-cancel">
                    <DraggableContentWrapper>
                        <DraggableContentCloseButton
                            className="draggable-cancel"
                            onClick={handleEmailClose}
                        >
                            <CloseRounded />
                        </DraggableContentCloseButton>
                        <DraggableContent>
                            <Label>Empfänger:</Label>
                            <RecipientsList>
                                {openedEmail.recipients.map(recipient => (
                                    <Recipient>
                                        {`${recipient.user?.lastName}, ${recipient.user?.firstName} (${recipient.email})`}
                                    </Recipient>
                                ))}
                            </RecipientsList>

                            <Label>Betreff:</Label>
                            <EmailSubject>{openedEmail.subject}</EmailSubject>
                            <EmailBody
                                dangerouslySetInnerHTML={{
                                    __html: openedEmail.body,
                                }}
                            />
                        </DraggableContent>
                    </DraggableContentWrapper>
                </Draggable>
            )}
            <StyledTable
                headerGroups={headerGroups}
                rows={rows}
                hiddenColumns={hiddenColumns}
                visibleColumns={visibleColumns}
                getTableProps={getTableProps}
                getTableBodyProps={getTableBodyProps}
                prepareRow={prepareRow}
            />
        </Wrapper>
    );
};

DriverLicenseSentEmailsTable.propTypes = {
    driverLicenseControl: PropTypes.object.isRequired,
    sentEmails: PropTypes.array.isRequired,
};

export default DriverLicenseSentEmailsTable;
